import axios from 'axios';
import { useMsal } from 'src/auth/firebaseMsalCompat';
import { useState } from 'react';
import { trackButtonClick } from '../services/telemetry';

import { getApiHost } from '../utils/getApiHost';
import { loginRequest } from '../config/authConfig';
import { useProjectActions } from './project/useProjectActions';

// Configuration interface for the custom hook
export interface UploadConfig {
  endpoint: string; // API endpoint. Use '{projectId}' as a placeholder if the endpoint needs the project id (e.g., '/fingering/upload/{projectId}').
  imagesEndpoint?: string; // API endpoint for multi-page image bundles (e.g., '/fingering/upload-images/{projectId}'). Required to use `uploadImagesDirectly`.
  validExtensions: string[]; // Valid file extensions (e.g., ['.xml', '.musicxml', '.mxl'] or ['.pdf'])
  acceptTypes: string; // HTML accept attribute for file input
  invalidFileMessage: string; // Error message for invalid files
  maxFileSize?: number; // Maximum file size in bytes (default: 10MB)
  invalidFileSizeMessage?: string; // Error message for files that are too large
  requiresProject?: boolean; // Whether this flow requires project creation (default: true)
}

const getExtension = (fileName: string): string =>
  fileName.toLowerCase().split('.').pop() ?? '';

const validateFileExtension = (
  fileName: string,
  validExtensions: string[]
): boolean => {
  const extension = getExtension(fileName);
  return extension ? validExtensions.includes(`.${extension}`) : false;
};

const validateFileSize = (file: File, maxFileSize: number): boolean => {
  return file.size <= maxFileSize;
};

interface RunUploadParams {
  endpoint: string;
  /** Populate the multipart body — a single `file` or many `files`. */
  appendBody: (formData: FormData) => void;
  /** Project name to create when `requiresProject !== false`. */
  projectName: string;
  /** Storage name to report when the server doesn't return one. */
  fallbackStorageName: string;
  /** Whether the result renders on the PDF-style result page. */
  isPdf: boolean;
  processingStatus: string;
  successStatus: string;
}

const useCustomFileUpload = (
  isAuthenticated: boolean,
  config: UploadConfig
) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [isFileValid, setIsFileValid] = useState<boolean>(true);
  const [isFileSizeValid, setIsFileSizeValid] = useState<boolean>(true);
  const { instance } = useMsal();
  const { createProject, deleteProject } = useProjectActions(isAuthenticated);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFile = event.target.files[0];
      const isValidExtension = validateFileExtension(
        selectedFile.name,
        config.validExtensions
      );

      const maxFileSize = config.maxFileSize || 10 * 1024 * 1024; // Default 10MB
      const isValidSize = validateFileSize(selectedFile, maxFileSize);

      trackButtonClick('FileUpload', 'Select File', {
        fileName: selectedFile.name,
      });

      setFile(selectedFile);
      setFileName(selectedFile.name);
      setIsFileValid(isValidExtension);
      setIsFileSizeValid(isValidSize);

      // Reset upload status when a new file is selected
      setUploadStatus('');
    }
  };

  // Multipart auth headers (Firebase idToken). Built in one place since every
  // upload path posts multipart/form-data with the same bearer token.
  const buildUploadHeaders = async () => {
    const authResult = await instance.acquireTokenSilent({ ...loginRequest });
    return {
      Authorization: `Bearer ${authResult.idToken}`,
      'Content-Type': 'multipart/form-data',
    };
  };

  // Map an upload failure to a user-facing message: 500s get a generic
  // "try later"; anything else surfaces the server-provided `detail`.
  const formatUploadError = (error: unknown): string => {
    const axiosError = error as {
      response?: { status?: number; data?: { detail?: string } };
      message?: string;
    };
    if (axiosError.response?.status === 500) {
      return 'Server error occurred. Please try again later.';
    }
    const detail = axiosError.response?.data?.detail || axiosError.message;
    return detail ? `Error: ${detail}` : 'Upload failed. Please try again.';
  };

  // Single implementation behind the three public upload functions:
  // (optionally) create a project, post the body, and roll the project back on
  // failure so a failed upload never leaves an empty project behind.
  const runUpload = async ({
    endpoint,
    appendBody,
    projectName,
    fallbackStorageName,
    isPdf,
    processingStatus,
    successStatus,
  }: RunUploadParams) => {
    let projectId: string | null = null;
    try {
      if (config.requiresProject !== false) {
        setUploadStatus('Creating project...');
        const createProjectResponse = await createProject(projectName);
        projectId = createProjectResponse.project_id;
      }

      const formData = new FormData();
      appendBody(formData);

      setUploadStatus(processingStatus);
      const headers = await buildUploadHeaders();

      let uploadUrl = `${getApiHost()}${endpoint}`;
      if (projectId) uploadUrl = uploadUrl.replace('{projectId}', projectId);

      const uploadResponse = await axios.post(uploadUrl, formData, { headers });
      if (uploadResponse.status !== 200) {
        throw new Error('File upload failed. Please try again.');
      }

      setUploadStatus(successStatus);
      const storageFileName =
        uploadResponse.data?.storageFileName || fallbackStorageName;
      return {
        success: true,
        projectId,
        storageFileName,
        isPdf,
        needsHandSelection: Boolean(uploadResponse.data?.needsHandSelection),
        layout: uploadResponse.data?.layout?.layout ?? null,
      };
    } catch (error) {
      if (projectId) await deleteProject(projectId);
      const message = formatUploadError(error);
      setUploadStatus(message);
      return { success: false, projectId: null, error: message };
    }
  };

  const uploadFile = async () => {
    if (!file) {
      alert('Please select a file first!');
      return;
    }
    if (!isFileValid || !isFileSizeValid) {
      alert('Please select a valid file!');
      return;
    }
    return runUpload({
      endpoint: config.endpoint,
      appendBody: (formData) => formData.append('file', file),
      projectName: fileName.split('.').slice(0, -1).join('.') || fileName,
      fallbackStorageName: fileName,
      isPdf: getExtension(fileName) === 'pdf',
      processingStatus: 'Uploading and processing file...',
      successStatus: 'File uploaded',
    });
  };

  const uploadFileDirectly = async (file: File) =>
    runUpload({
      endpoint: config.endpoint,
      appendBody: (formData) => formData.append('file', file),
      projectName: file.name.split('.').slice(0, -1).join('.') || file.name,
      fallbackStorageName: file.name,
      isPdf: getExtension(file.name) === 'pdf',
      processingStatus: 'Uploading and processing file...',
      successStatus: 'File uploaded',
    });

  // Ordered list of page images uploaded as one project. Posts every page
  // under the same `files` multipart key so the backend receives a
  // `List[UploadFile]`, and targets `config.imagesEndpoint`.
  const uploadImagesDirectly = async (files: File[]) => {
    if (!config.imagesEndpoint) {
      throw new Error('imagesEndpoint is not configured for this upload flow');
    }
    // No single "file name" for a multi-page bundle — name the project after
    // the first page, falling back to a date stamp. Users can rename later.
    const firstBase = files[0]?.name.split('.').slice(0, -1).join('.');
    return runUpload({
      endpoint: config.imagesEndpoint,
      appendBody: (formData) =>
        files.forEach((f) => formData.append('files', f)),
      projectName: firstBase || `Scan ${new Date().toLocaleDateString()}`,
      fallbackStorageName: files[0]?.name ?? '',
      // Image (OMR) jobs always render on the PDF-style result page.
      isPdf: true,
      processingStatus: 'Uploading and processing images...',
      successStatus: 'Images uploaded',
    });
  };

  return {
    uploadStatus,
    handleFileChange,
    uploadFile,
    uploadFileDirectly,
    uploadImagesDirectly,
    fileName,
    setUploadStatus,
    isFileValid,
    isFileSizeValid,
  };
};

export { useCustomFileUpload };
